import React, { ReactElement, cloneElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';

import Alert from '@mui/material/Alert';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';

import { Loader, useMatomo } from '@omnigenbiodata/ui';

import { StoreStatuses } from '../../../../../../core/types/common.types';

interface EhrTabProps {
  data: {
    error: any;
    status: StoreStatuses;
    response: any;
  };
  showCaveat?: boolean;
  showCount?: boolean;
  icon: ReactElement;
  type: string;
  title: string;
  children: ReactElement;
}

const EhrTab = ({ data, showCaveat, showCount, type, title, icon, children }: EhrTabProps) => {
  const { trackEvent } = useMatomo();
  const { t } = useTranslation('portal');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRows] = useState(10);

  const handlePagination = (_event: any, newPage: number) => {
    setPage(newPage);
    trackEvent({ category: type, action: `set-page-${newPage}` });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);

    setRows(nextRowsPerPage);
    setPage(0);

    trackEvent({
      category: type,
      action: `set-rows-${event.target.value}`,
    });
  };

  const hasRows = Boolean(data.response?.length);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          pt: { xs: 2, md: 2.5 },
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'info.lighter',
              color: 'info.dark',
              border: 1,
              borderColor: 'info.light',
              flexShrink: 0,
              '& svg': {
                fontSize: 28,
              },
            }}
          >
            {icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                fontWeight: 900,
                lineHeight: 1.15,
              }}
            >
              {title}
            </Typography>

            {showCount && hasRows && (
              <Typography
                variant="body2"
                component="p"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                }}
              >
                {data.response.length === 1
                  ? t(`health.${type}.single`, { total: data.response.length })
                  : t(`health.${type}.multiple`, { total: data.response.length })}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        {showCaveat && (
          <Alert
            severity="info"
            icon={<InfoOutlined />}
            sx={{
              mb: 2.5,
              borderRadius: 3,
              border: 1,
              borderColor: 'info.light',
              alignItems: 'flex-start',
              '& .MuiAlert-icon': {
                mt: 0.35,
              },
              '& .MuiAlert-message': {
                width: '100%',
                '& p': {
                  mt: 0,
                  mb: 1,
                },
                '& p:last-child': {
                  mb: 0,
                },
              },
            }}
          >
            <ReactMarkdown>{t(`health.${type}.caveat`)}</ReactMarkdown>
          </Alert>
        )}

        {data.error && (
          <Alert
            severity="error"
            icon={<ErrorOutlineOutlined />}
            sx={{
              mb: 2.5,
              borderRadius: 3,
              border: 1,
              borderColor: 'error.light',
              alignItems: 'center',
              '& .MuiAlert-message': {
                fontWeight: 700,
              },
            }}
          >
            {t('health.error')}
          </Alert>
        )}

        {data.response && (
          <>
            {cloneElement(children, { page, rowsPerPage })}

            {data.response.length > 10 && (
              <Box
                sx={{
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <TablePagination
                  component="div"
                  count={data.response.length}
                  page={page}
                  onPageChange={handlePagination}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  SelectProps={{ id: 'change-page' }}
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      px: { xs: 1.5, md: 2 },
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontWeight: 700,
                      color: 'text.secondary',
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <Loader isVisible={data.status === StoreStatuses.BUSY} label={t('health.loader') as string} />
    </Box>
  );
};

export default EhrTab;
